import { useRef } from "react";
import { Bold, Italic, Link as LinkIcon, List, ListOrdered, Heading2, Quote } from "lucide-react";

/**
 * RichTextEditor — minimal Markdown-aware editor.
 * A textarea with a toolbar that inserts Markdown syntax around the current selection.
 * The output is plain text + Markdown which is rendered server-/render-side by
 * BlockRenderer (via marked + DOMPurify).
 */
export default function RichTextEditor({ value, onChange, placeholder, minHeight = "160px", testId = "rte" }) {
  const ref = useRef(null);

  const surround = (before, after = before) => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = ta.value;
    const selected = text.substring(start, end);
    const next = text.substring(0, start) + before + selected + after + text.substring(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length, end + before.length);
    });
  };

  const prefixLines = (prefix) => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = ta.value;
    // Find line starts in selection
    const lineStart = text.lastIndexOf("\n", start - 1) + 1;
    const before = text.substring(0, lineStart);
    const middle = text.substring(lineStart, end);
    const after = text.substring(end);
    const updated = middle.split("\n").map((l) => prefix + l).join("\n");
    onChange(before + updated + after);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(lineStart, lineStart + updated.length);
    });
  };

  const insertLink = () => {
    const ta = ref.current;
    if (!ta) return;
    const url = window.prompt("URL eingeben:", "https://");
    if (!url) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = ta.value;
    const selected = text.substring(start, end) || "Linktext";
    const md = `[${selected}](${url})`;
    onChange(text.substring(0, start) + md + text.substring(end));
    requestAnimationFrame(() => ta.focus());
  };

  const btnCls = "p-1.5 rounded hover:bg-[var(--mv-surface)] transition-colors";
  return (
    <div className="border rounded-lg overflow-hidden" style={{ borderColor: "var(--mv-border)" }} data-testid={testId}>
      <div className="flex items-center gap-0.5 px-2 py-1 border-b" style={{ borderColor: "var(--mv-border)", background: "var(--mv-surface, #fafafa)" }}>
        <button type="button" data-testid={`${testId}-bold`} className={btnCls} onClick={() => surround("**")} title="Fett (Strg+B)"><Bold className="w-3.5 h-3.5" strokeWidth={1.75} /></button>
        <button type="button" data-testid={`${testId}-italic`} className={btnCls} onClick={() => surround("*")} title="Kursiv (Strg+I)"><Italic className="w-3.5 h-3.5" strokeWidth={1.75} /></button>
        <span className="w-px h-4 mx-1" style={{ background: "var(--mv-border)" }} />
        <button type="button" data-testid={`${testId}-h2`} className={btnCls} onClick={() => prefixLines("## ")} title="Zwischenüberschrift"><Heading2 className="w-3.5 h-3.5" strokeWidth={1.75} /></button>
        <button type="button" data-testid={`${testId}-ul`} className={btnCls} onClick={() => prefixLines("- ")} title="Liste"><List className="w-3.5 h-3.5" strokeWidth={1.75} /></button>
        <button type="button" data-testid={`${testId}-ol`} className={btnCls} onClick={() => prefixLines("1. ")} title="Nummerierte Liste"><ListOrdered className="w-3.5 h-3.5" strokeWidth={1.75} /></button>
        <button type="button" data-testid={`${testId}-quote`} className={btnCls} onClick={() => prefixLines("> ")} title="Zitat"><Quote className="w-3.5 h-3.5" strokeWidth={1.75} /></button>
        <span className="w-px h-4 mx-1" style={{ background: "var(--mv-border)" }} />
        <button type="button" data-testid={`${testId}-link`} className={btnCls} onClick={insertLink} title="Link"><LinkIcon className="w-3.5 h-3.5" strokeWidth={1.75} /></button>
        <span className="ml-auto text-[10px] opacity-50 pr-1">Markdown unterstützt</span>
      </div>
      <textarea
        ref={ref}
        data-testid={`${testId}-textarea`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "b") { e.preventDefault(); surround("**"); }
          if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "i") { e.preventDefault(); surround("*"); }
        }}
        placeholder={placeholder}
        className="w-full p-3 text-sm leading-relaxed outline-none resize-y"
        style={{ minHeight, background: "transparent", color: "var(--mv-text)" }}
      />
    </div>
  );
}
